/**
 * categoryAccent — Task 14 gave the four `LabelingCategoryKey`s distinct
 * per-category hues (three non-blue tints alongside the existing signal
 * blue). Task 15 (Andrea feedback, 2026-07-08) reverted that: the "square
 * by the title" is BLUE for every category, and the overview tree's accent
 * palette is restricted to blue / red / green only — every non-blue/red/
 * green tint is gone. All four keys below now resolve to the same
 * `signal` (blue) accent. The `CategoryAccent` shape + `categoryAccent()`
 * helper are kept as-is (rather than collapsing to a single constant) so
 * call sites don't need to change, and so a future per-category identity
 * change (if ever revisited, staying within blue/red/green) is a one-file
 * diff again.
 *
 * `edge`/`text` are for small identity surfaces (a tile's left bar, a mono
 * key, a header marker), `tint` is a ~10%-alpha wash for hover states,
 * never a large saturated fill.
 */

import type { LabelingCategoryKey } from "../../../utils/labelingCategories";

export interface CategoryAccent {
  /** Solid bar/edge color (`bg-*`). */
  edge: string;
  /** Accent text color (mono key / eyebrow) (`text-*`). */
  text: string;
  /** Faint background wash, ~10% alpha (e.g. `bg-emerald-500/10`-ish). */
  tint: string;
  /** Accent border color (`border-*`). */
  border: string;
}

const BLUE_ACCENT: CategoryAccent = {
  edge: "bg-signal",
  text: "text-signal-text-tint",
  tint: "bg-signal-tint",
  border: "border-signal-stroke/40",
};

export const CATEGORY_ACCENT: Record<LabelingCategoryKey, CategoryAccent> = {
  "3d-segmentation": BLUE_ACCENT,
  "segmentation-studies": BLUE_ACCENT,
  "reporter-studies": BLUE_ACCENT,
  "triage-validation": BLUE_ACCENT,
  other: BLUE_ACCENT,
};

export function categoryAccent(key: LabelingCategoryKey): CategoryAccent {
  return CATEGORY_ACCENT[key];
}
